<script>
import baseMixin from '@/mixins/baseMixin'
import { mapState } from 'vuex'
import AdaptiveCard from '@/components/releaseNotes/AdaptiveCard.vue'
import PreviewWarnings from '@/components/releaseNotes/PreviewWarnings.vue'
import Dialog from '@/components/general/dialog.vue'
import Spinner from '@/components/general/spinner.vue'
import ReleaseNotes from '@/components/releaseNotes/ReleaseNotesCard.vue'
import JsonEditor from '@/components/releaseNotes/JsonEditor.vue'

export default {
  components: {
    ReleaseNotes,
    AdaptiveCard,
    PreviewWarnings,
    Dialog,
    Spinner,
    JsonEditor,
  },
  mixins: [baseMixin],
  data: function () {
    return {
      tab: 0,
      search: '',
      isPreviewLoading: false,
      isPreviewSaving: false,
      noTitle: 'No Release Title available',
      noDescription: 'No Release Description available',
    }
  },

  computed: {
    ...mapState('releases', ['releases', 'preview']),

    tabItems() {
      return [
        {
          title: 'Release Notes',
          component: ReleaseNotes,
        },
        {
          title: 'Adaptive card',
          component: AdaptiveCard,
        },
        {
          title: 'Json Editor',
          component: JsonEditor,
        },
      ]
    },
    previewSaved() {
      return this.releases.some(
        (release) => release.releaseId === this.preview.releaseId
      )
    },
    previewActions() {
      return [
        {
          title: 'Save Preview',
          action: () => this.handleSave(),
          condition: this?.preview?.releaseId,
        },
        {
          title: 'Resolve Titles',
          action: () => this.resolveTitles(),
          condition: this?.warnings?.some(
            (item) => item.releaseTitle === this.noTitle
          ),
        },
        {
          title: 'Post To Teams',
          action: () => this.postToTeams(),
          condition:
            !this?.releases
              ?.find((item) => item.releaseId === this.preview.releaseId)
              ?.releaseData?.workItems.some(
                (item) =>
                  item.releaseTitle === this.noTitle ||
                  item.releaseDescription === this.noDescription
              ) ?? false,
        },
      ]
    },

    warnings() {
      return (
        this?.preview?.releaseData?.workItems
          ?.filter(
            (item) =>
              item.releaseTitle === this.noTitle ||
              item.releaseDescription === this.noDescription
          )
          .sort((item) => item.author) ?? []
      )
    },
  },

  methods: {
    async getPreview() {
      this.isPreviewLoading = true
      const releaseId = this.search

      if (this.search !== this.preview?.releaseId) {
        this.$store.commit('releases/SET_STATE', {
          key: 'savedPreview',
          value: false,
        })
      }
      await this.$store.dispatch('releases/getPreview', releaseId)

      this.isPreviewLoading = false
    },

    async savePreview() {
      this.isPreviewSaving = true
      this.resolveTitles()
      await this.$store.dispatch('releases/savePreview', this.preview)
      this.isPreviewSaving = false
      this.$refs.saveDialog.close()
      this.cache++
    },

    async handleSave() {
      if (!this.warnings.length) {
        await this.savePreview()
        return
      }

      this.$refs.saveDialog.open()
    },
    updateWorkItem(workItem) {
      const previewCopy = { ...this.preview }
      const index = previewCopy.releaseData.workItems.findIndex(
        (item) => item.id === workItem.id
      )
      if (index > -1) {
        previewCopy.releaseData.workItems[index] = workItem
        this.$store.commit('releases/SET_STATE', {
          key: 'preview',
          value: previewCopy,
        })
      }
    },

    resolveTitles() {
      if (!this?.preview?.releaseData.workItems?.length) {
        return
      }

      const updatedWorkItems = this.preview.releaseData.workItems.map((wi) => ({
        ...wi,
        releaseTitle:
          wi.releaseTitle === this.noTitle ? wi.title : wi.releaseTitle,
      }))

      this.$store.commit('releases/SET_STATE', {
        key: 'preview',
        value: {
          ...this.preview,
          releaseData: {
            ...this.preview.releaseData,
            workItems: updatedWorkItems,
          },
        },
      })
    },

    async postToTeams() {
      this.isPreviewLoading = true
      await this.$store.dispatch(
        'releases/sendTeamsMessage',
        this.preview.releaseId
      )
      this.isPreviewLoading = false
    },
  },
}
</script>

<template>
  <!-- Header, search and actions -->

  <v-container>
    <div class="d-flex w-full align-center">
      <h1>Release Preview </h1>
      <transition name="fade-left">
        <div v-if="previewSaved" class="saved-icon pa-2 ml-4">
          <v-icon class="white--text" background-color="success"
            >mdi-check</v-icon
          >
        </div>
      </transition>
    </div>
    <v-container class="mb-8 d-flex align-end">
      <v-text-field
        v-model="search"
        class="preview-search"
        prepend-inner-icon="mdi-magnify"
        clearable
        placeholder="Please enter a release ID"
        hide-details
        @keyup.enter="getPreview"
      />
      <v-btn
        class="py-6 ml-4"
        depressed
        color="primary"
        large
        :disabled="isPreviewSaving || isPreviewLoading"
        @click="getPreview"
        >Get Preview</v-btn
      >
      <v-menu
        v-if="previewActions.some((item) => item.condition)"
        offset-y
        open-on-hover
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="pa-6 px-14 ml-4"
            color="primary"
            outlined
            v-bind="attrs"
            v-on="on"
          >
            More
          </v-btn>
        </template>
        <v-list class="pa-0">
          <v-list-item
            v-for="(item, index) in previewActions"
            :key="index"
            :disabled="!item.condition"
            class="action-item py-2 px-4"
            @click="item.action"
          >
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-container>

    <!-- Warnings -->

    <transition name="fade-up">
      <v-container v-if="preview.releaseId && !isPreviewLoading">
        <PreviewWarnings
          v-if="warnings.length"
          class="mb-8"
          :preview="preview"
          :warnings="warnings"
          @update-work-item="updateWorkItem"
        />

        <!-- tab navigation and content -->

        <v-tabs v-model="tab" background-color="transparent">
          <v-tab v-for="({ title }, index) in tabItems" :key="index">
            {{ title }}
          </v-tab>
        </v-tabs>
        <v-tabs-items
          v-if="!isPreviewSaving"
          id="preview-tabs-items"
          v-model="tab"
        >
          <v-tab-item v-for="({ component }, index) in tabItems" :key="index">
            <component :is="component" :release="preview" />
          </v-tab-item>
        </v-tabs-items>
        <Spinner v-else size="40" />
      </v-container>
    </transition>
    <Spinner v-if="isPreviewLoading" class="mt-10" size="60" />

    <!-- save preview with warnings dialog -->
    <Dialog
      ref="saveDialog"
      title="Save with these warnings?"
      action-title="Save"
      :action="savePreview"
      :loading="isPreviewSaving"
      action-subtitle="* Saving will replace missing Release Titles with existing Titles"
    >
      <template v-slot:content>
        <p v-for="wi in warnings" :key="wi.Id" class="text-center">
          <span class="mr-4">
            <v-icon
              v-if="wi.releaseDescription === noDescription"
              color="error"
              size="32"
              >mdi-alert-circle-outline</v-icon
            >
          </span>
          <span class="font-weight-bold">{{ wi.id }} -</span>
          <span v-if="wi.releaseTitle === noTitle" class="mx-4"
            >No Release Title</span
          >
          <span
            v-if="wi.releaseDescription === noDescription"
            class="mx-4 error--text"
            >No Release Description</span
          >
        </p>
      </template>
    </Dialog>
  </v-container>
</template>

<style lang="scss" scoped>
.preview-search {
  width: 100%;
  max-width: 600px;
}

#preview-tabs-items {
  background-color: transparent;
}

.action-item:hover {
  cursor: pointer;
  background: #c7c7c7;
}

.saved-icon {
  height: min-content;
  background-color: #4caf50;
  border-radius: 100%;
}
</style>
